import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import client from "../../services/apollo/apolloClient/index";
import { RECORD_AI_CONSENT } from "../../services/apollo/mutations/userMutations";
import {
  CONSENT_VERSION,
  clearLocalConsent,
  hasValidAiConsent,
  subscribeToConsentChanges,
} from "../../utils/aiConsent";
import { clearResults as clearAiSearchResults } from "../../slices/aiSearchSlice";
import { openCookiePreferences } from "../../utils/cookieConsent";
import AccountSettingsModal, { SETTINGS_TYPES } from "../AccountSettingsModal/AccountSettingsModal";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";

const SUPPORT_EMAIL = "support@allanimalapp.com";

const linkClass =
  "text-xs sm:text-sm text-muted-foreground hover:text-foreground hover:underline transition-colors";

const LegalFooter = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [hasConsent, setHasConsent] = useState(hasValidAiConsent());
  const [withdrawn, setWithdrawn] = useState(false);
  const [showLanguage, setShowLanguage] = useState(false);

  const legalLinks = [
    { id: "terms-summary", label: t("footer_terms_summary", "Terms Summary"), to: "/terms-summary", type: "route" },
    { id: "terms", label: t("footer_full_terms", "Full Terms"), to: "/terms", type: "route" },
    { id: "privacy", label: t("privacy_policy", "Privacy Policy"), to: "/privacy", type: "route" },
    { id: "dmca", label: t("footer_dmca", "DMCA"), to: "/dmca", type: "route" },
    { id: "contact", label: t("footer_contact", "Contact"), href: `mailto:${SUPPORT_EMAIL}`, type: "mail" },
  ];

  useEffect(() => {
    const sync = () => setHasConsent(hasValidAiConsent());
    sync();
    return subscribeToConsentChanges(sync);
  }, []);

  const handleWithdrawConsent = () => {
    clearLocalConsent();
    dispatch(clearAiSearchResults());
    if (isAuthenticated) {
      client
        .mutate({
          mutation: RECORD_AI_CONSENT,
          variables: { aiConsent: false, consentVersion: CONSENT_VERSION },
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.warn(
            "Failed to withdraw AI consent on the server:",
            error?.message || error
          );
        });
    }
    setWithdrawn(true);
  };

  return (
    <footer
      role="contentinfo"
      className="relative z-20 w-full border-t border-border/60 bg-background px-4 pt-6 pb-28 sm:pb-32 mt-8"
    >
      <nav
        aria-label={t("footer_legal_aria", "Legal")}
        className="flex flex-col items-center gap-y-2"
      >
        {/* First line - legal links */}
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          {legalLinks.map((link) =>
            link.type === "route" ? (
              <Link key={link.id} to={link.to} className={linkClass}>
                {link.label}
              </Link>
            ) : (
              <a key={link.id} href={link.href} className={linkClass}>
                {link.label}
              </a>
            )
          )}
        </div>

        {/* Second line - preferences */}
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          <button
            type="button"
            onClick={openCookiePreferences}
            className={linkClass}
          >
            {t("footer_cookie_settings", "Cookie Settings")}
          </button>
          <button
            type="button"
            onClick={() => setShowLanguage(true)}
            className={linkClass}
          >
            {t("language", "Language")}
          </button>
          {hasConsent && (
            <button
              type="button"
              onClick={handleWithdrawConsent}
              className={linkClass}
            >
              {t("footer_withdraw_ai_consent", "Withdraw AI Consent")}
            </button>
          )}
        </div>
      </nav>
      {withdrawn && !hasConsent && (
        <p
          role="status"
          className="mt-2 text-center text-[11px] sm:text-xs text-muted-foreground"
        >
          {t("footer_ai_consent_withdrawn", "AI consent withdrawn. You'll be asked again next time you use AI search.")}
        </p>
      )}
      <p className="mt-4 text-center text-[11px] sm:text-xs text-muted-foreground/80">
        © 2026 All Animal App® / CMCI Consulting &amp; Management Group LLC. All rights reserved.
      </p>

      {typeof document !== "undefined" &&
        createPortal(
          <AccountSettingsModal
            isOpen={showLanguage}
            onClose={() => setShowLanguage(false)}
            type={SETTINGS_TYPES.LANGUAGE}
          >
            <LanguageSwitcher />
          </AccountSettingsModal>,
          document.body
        )}
    </footer>
  );
};

export default LegalFooter;
